import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FolderKanban, Plus } from 'lucide-react';
import { useState } from 'react';
import { useMe } from '~/api/auth';
import { type Project, type ProjectStatus, projectsQueryOptions } from '~/api/projects';
import { useServerClock } from '~/api/serverClock';
import { ProjectCard } from '~/features/projects/ProjectCard';
import { ProjectDialog } from '~/features/projects/ProjectDialog';
import { calendarDate } from '~/lib/format';
import { Button } from '~/ui/Button';
import { Chips } from '~/ui/Chips';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { PageHeader } from '~/ui/PageHeader';
import { Skeleton } from '~/ui/Skeleton';
import { Switch } from '~/ui/Switch';

type Who = 'all' | 'mine' | 'team' | 'personal';

const SECTIONS: { status: ProjectStatus; title: string; hint: string }[] = [
  { status: 'active', title: 'Ongoing', hint: 'Work is happening now.' },
  { status: 'planning', title: 'Upcoming', hint: 'Planned, not started yet.' },
  { status: 'on_hold', title: 'On hold', hint: 'Paused for now.' },
  { status: 'done', title: 'Finished', hint: 'Reached their goal.' },
  { status: 'archived', title: 'Archived', hint: 'Put away; history kept.' },
];

export const Route = createFileRoute('/_app/projects/')({
  component: ProjectsPage,
});

function ProjectsPage() {
  const me = useMe();
  const navigate = useNavigate();
  const now = useServerClock(60_000);
  const today = calendarDate(now, me.person.tz ?? me.workspace.tz);
  const projects = useQuery(projectsQueryOptions());
  const [who, setWho] = useState<Who>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);

  const fits = (w: Who, p: Project) =>
    w === 'all'
      ? true
      : w === 'mine'
        ? p.members.some((m) => m.personId === me.person.id)
        : p.kind === w;
  const all = projects.data ?? [];
  const rows = all.filter((p) => fits(who, p));
  const count = (w: Who) => all.filter((p) => fits(w, p)).length;
  const sections = SECTIONS.filter((s) => s.status !== 'archived' || showArchived);
  const visible = rows.filter((p) => p.status !== 'archived' || showArchived);

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader
        eyebrow="Work"
        title="Projects"
        subtitle="Every project with its board and its people: what is going on now, what comes next, what is finished."
        actions={
          <Button
            icon={<Plus size={16} aria-hidden />}
            data-tour="project-new"
            onClick={() => setCreating(true)}
          >
            New project
          </Button>
        }
      />
      <div
        className="mb-6 flex flex-wrap items-center justify-between gap-3"
        data-tour="project-filter"
      >
        <Chips
          label="Whose projects"
          value={who}
          onChange={setWho}
          options={[
            { value: 'all', label: 'All', count: count('all') },
            { value: 'mine', label: 'Mine', count: count('mine') },
            { value: 'team', label: 'Team', count: count('team') },
            { value: 'personal', label: 'Personal', count: count('personal') },
          ]}
        />
        <div className="w-48">
          <Switch label="Show archived" checked={showArchived} onChange={setShowArchived} />
        </div>
      </div>
      {projects.isPending ? (
        <Skeleton lines={6} />
      ) : projects.isError ? (
        <ErrorNotice error={projects.error} onRetry={() => projects.refetch()} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<FolderKanban aria-hidden />}
          title="No projects here yet."
          body="Start one for a team, or a personal one for your own work."
          action={
            <Button size="sm" onClick={() => setCreating(true)}>
              Start a project
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-8">
          {sections.map((section) => {
            const list = rows.filter((p) => p.status === section.status);
            if (list.length === 0) return null;
            return (
              <section key={section.status} aria-labelledby={`projects-${section.status}`}>
                <div className="mb-3 flex flex-wrap items-baseline gap-2">
                  <h2 id={`projects-${section.status}`} className="text-lg">
                    {section.title}
                  </h2>
                  <span className="font-mono text-xs text-ink-3 tabular">{list.length}</span>
                  <span className="text-xs text-ink-3">{section.hint}</span>
                </div>
                <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {list.map((p, i) => (
                    <li key={p.id}>
                      <ProjectCard project={p} today={today} delay={Math.min(i, 8) * 40} />
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
      <ProjectDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => void navigate({ to: '/projects/$id', params: { id } })}
      />
    </div>
  );
}
